// src/components/Hero/Hero.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheckCircle,
  FaChartLine,
  FaBolt,
  FaGoogle,
  FaStar,
  FaShieldAlt,
} from "react-icons/fa";
import { useLocalContext } from "../../context/LocalContext";
import "./Hero.css";

const Hero = ({ onSubmitLead }) => {
  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo?.name || "Digitalcoreweb";

  const [activeTab, setActiveTab] = useState("dashboard"); // 'dashboard' | 'consultation'
  const [form, setForm] = useState({ name: "", phone: "", service: "" });
  const [status, setStatus] = useState({ loading: false, ok: "", err: "" });

  const onChange = (e) => {
    setStatus({ loading: false, ok: "", err: "" });
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      return setStatus({
        loading: false,
        ok: "",
        err: "Please provide your name and phone number.",
      });
    }

    try {
      setStatus({ loading: true, ok: "", err: "" });
      await onSubmitLead?.({ ...form, source: "hero_audit_lead" });
      setStatus({
        loading: false,
        ok: "Audit request received! We will reach out shortly.",
        err: "",
      });
      setForm({ name: "", phone: "", service: "" });
    } catch {
      setStatus({
        loading: false,
        ok: "",
        err: "Submission failed. Please try again or call us.",
      });
    }
  };

  return (
    <section className="heroAboutTheme">
      {/* Background ambient light mesh & subtle grid */}
      <div className="hero-glow-1 bg-radial-glow" />
      <div className="hero-glow-2 bg-radial-glow" />
      <div className="hero-grid-pattern" />

      <div className="heroAboutTheme-wrap">
        {/* LEFT COLUMN: HERO CONTENT */}
        <motion.div
          className="heroAboutTheme-left"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="hero-badge">
            <span className="hero-badge-pulse" />
            <span className="hero-badge-text">
              2026 Growth &amp; Digital Marketing Partner
            </span>
          </div>

          <h1 className="hero-heading">
            Grow Your Brand. <br />
            <span className="text-gradient">Reach More Customers.</span> <br />
            <span className="hero-heading-sub">Convert Visitors into Revenue.</span>
          </h1>

          <p className="hero-description">
            <strong>{brandName}</strong> engineers high-converting websites,
            data-driven SEO, performance marketing, and multi-channel campaigns
            that turn online attention into predictable, compounding revenue.
          </p>

          {/* PRIMARY & SECONDARY CTAS */}
          <div className="hero-cta-group">
            <button
              className="hero-btn-primary"
              onClick={() => setActiveTab("consultation")}
            >
              <span>Get Started</span>
              <FaArrowRight className="btn-arrow" />
            </button>

            <button
              className="hero-btn-secondary"
              onClick={() => navigate("/services")}
            >
              <span>Explore Services</span>
            </button>
          </div>

          {/* KEY BENEFIT PILLS */}
          <ul className="hero-feature-list">
            <li>
              <FaCheckCircle className="feat-icon" />
              <span>Conversion-focused design &amp; UX</span>
            </li>
            <li>
              <FaCheckCircle className="feat-icon" />
              <span>Technical SEO &amp; high-speed performance</span>
            </li>
            <li>
              <FaCheckCircle className="feat-icon" />
              <span>Measurable ROI with real-time reporting</span>
            </li>
          </ul>

          {/* SOCIAL PROOF & STATS ROW */}
          <div className="hero-proof-bar">
            <div className="hero-rating-box">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="star-gold" />
                ))}
              </div>
              <div className="rating-text">
                <strong>4.9/5 Rating</strong> from 80+ clients
              </div>
            </div>

            <div className="hero-stat-pill">
              <strong>150+</strong>
              <span>Projects Launched</span>
            </div>

            <div className="hero-stat-pill">
              <strong>+280%</strong>
              <span>Avg. Traffic Growth</span>
            </div>
          </div>
        </motion.div>

        {/* RIGHT COLUMN: 2026 MARKETING DASHBOARD & INTERACTIVE CARD */}
        <motion.div
          className="heroAboutTheme-right"
          initial={{ opacity: 0, scale: 0.95, y: 25 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* FLOATING BADGE 1: GROWTH RATE */}
          <motion.div
            className="floating-card floating-card-top"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="float-icon-box growth">
              <FaChartLine />
            </div>
            <div>
              <div className="float-label">Conversion Rate</div>
              <div className="float-value">+284% <span className="trend">↑ ROI</span></div>
            </div>
          </motion.div>

          {/* FLOATING BADGE 2: GOOGLE RANK */}
          <motion.div
            className="floating-card floating-card-bottom"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
          >
            <div className="float-icon-box rank">
              <FaGoogle />
            </div>
            <div>
              <div className="float-label">Google Search</div>
              <div className="float-value">#1 Organic Rank</div>
            </div>
          </motion.div>

          {/* MAIN BENTO CARD WITH TABS */}
          <div className="hero-main-card">
            {/* CARD HEADER WITH TABS */}
            <div className="card-tab-nav">
              <button
                className={`tab-btn ${activeTab === "dashboard" ? "active" : ""}`}
                onClick={() => setActiveTab("dashboard")}
              >
                <FaChartLine /> Marketing Analytics
              </button>
              <button
                className={`tab-btn ${activeTab === "consultation" ? "active" : ""}`}
                onClick={() => setActiveTab("consultation")}
              >
                <FaBolt /> Free Consultation
              </button>
            </div>

            {/* TAB 1: VISUAL MARKETING DASHBOARD */}
            {activeTab === "dashboard" ? (
              <div className="dashboard-view">
                <div className="dash-header">
                  <div>
                    <span className="dash-badge">Campaign Performance</span>
                    <h3 className="dash-title">Q1 Growth Trajectory</h3>
                  </div>
                  <span className="dash-live-indicator">
                    <span className="live-dot" /> Live Data
                  </span>
                </div>

                {/* METRICS ROW */}
                <div className="dash-metrics-grid">
                  <div className="metric-box">
                    <span className="m-label">Monthly Visitors</span>
                    <strong className="m-value">148.6K</strong>
                    <span className="m-delta positive">+42.8%</span>
                  </div>
                  <div className="metric-box">
                    <span className="m-label">Lead Conversions</span>
                    <strong className="m-value">2,840</strong>
                    <span className="m-delta positive">+68.4%</span>
                  </div>
                  <div className="metric-box">
                    <span className="m-label">Avg. ROAS</span>
                    <strong className="m-value">5.4x</strong>
                    <span className="m-delta positive">+31.2%</span>
                  </div>
                </div>

                {/* GRAPH VISUAL */}
                <div className="dash-graph-box">
                  <div className="graph-label">Organic &amp; Paid Pipeline (6 Months)</div>
                  <div className="graph-bars">
                    {[
                      { m: "Nov", h1: "40%", h2: "25%" },
                      { m: "Dec", h1: "55%", h2: "38%" },
                      { m: "Jan", h1: "72%", h2: "50%" },
                      { m: "Feb", h1: "88%", h2: "65%" },
                      { m: "Mar", h1: "100%", h2: "82%" },
                    ].map((bar, idx) => (
                      <div className="bar-col" key={idx}>
                        <div className="bar-track">
                          <motion.div
                            className="bar-fill blue"
                            initial={{ height: 0 }}
                            animate={{ height: bar.h1 }}
                            transition={{ duration: 0.8, delay: 0.1 * idx }}
                          />
                          <motion.div
                            className="bar-fill purple"
                            initial={{ height: 0 }}
                            animate={{ height: bar.h2 }}
                            transition={{ duration: 0.8, delay: 0.15 * idx }}
                          />
                        </div>
                        <span className="bar-month">{bar.m}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* QUICK CTA IN DASHBOARD */}
                <div className="dash-footer-cta">
                  <div className="cta-txt">
                    Ready to scale your business with numbers like these?
                  </div>
                  <button
                    className="dash-action-btn"
                    onClick={() => setActiveTab("consultation")}
                  >
                    Request Free Audit
                  </button>
                </div>
              </div>
            ) : (
              /* TAB 2: CONSULTATION / LEAD FORM (PRESERVING FUNCTIONALITY) */
              <div className="consultation-view">
                <div className="consult-head">
                  <h3>Get Your Free Growth Audit</h3>
                  <p>
                    Share your requirements and we will review your digital footprint
                    with clear next steps.
                  </p>
                </div>

                <form className="consult-form" onSubmit={handleSubmit}>
                  <div className="input-group">
                    <label>Full Name *</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder="e.g. Rahul Sharma"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label>Phone Number *</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="e.g. +91 98765 43210"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label>Primary Service Needed</label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={onChange}
                    >
                      <option value="">Select a service</option>
                      <option>SEO &amp; Search Visibility</option>
                      <option>UI/UX &amp; Website Designing</option>
                      <option>Full-Stack Web &amp; App Development</option>
                      <option>Paid Ads &amp; Performance Marketing</option>
                      <option>Social Media Marketing</option>
                      <option>Local Business Growth</option>
                    </select>
                  </div>

                  {status.err && <div className="form-msg err">{status.err}</div>}
                  {status.ok && <div className="form-msg ok">{status.ok}</div>}

                  <button
                    type="submit"
                    className="consult-submit-btn"
                    disabled={status.loading}
                  >
                    {status.loading ? "Submitting Request…" : "Get My Free Plan →"}
                  </button>
                </form>

                <div className="consult-security">
                  <FaShieldAlt /> 100% Confidential · Free Assessment · No Obligation
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
